'use client'

import { useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BellRingIcon, TrashIcon, CheckCheckIcon, CheckIcon, RefreshCwIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { LoadingState, DeleteConfirmationDialog } from '@/components/partials'
import { NotificationItem, NotificationsFilters } from '@/components/partials/notifications'
import {
  useNotifications,
  useSetReadNotifications,
  useDeleteNotification,
  useBulkDeleteNotifications
} from '@/hooks/use-notifications'
import { BulkDeleteResponse } from '@/types/general'
import { useNotificationsStore } from '@/store'
import { useSearchParams } from 'next/navigation'
import { DashboardResponseType } from '@/types/dashboard'

const NotificationSkeleton = () => (
  <div className="flex items-start gap-4 py-4">
    <div className="flex shrink-0 items-center gap-2 self-center pl-2">
      <Skeleton className="h-4 w-4 rounded" />
    </div>
    <Skeleton className="ml-2 h-9 w-9 shrink-0 rounded-full" />
    <div className="min-w-0 flex-1 space-y-2 py-1">
      <Skeleton className="h-3.5 w-2/5" />
      <Skeleton className="h-3 w-3/5" />
      <Skeleton className="h-3 w-1/4" />
    </div>
    <div className="flex shrink-0 items-center gap-2 self-start pt-1 pr-2">
      <Skeleton className="h-8 w-8 rounded" />
      <Skeleton className="h-8 w-8 rounded" />
    </div>
  </div>
)

const List = () => {
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()

  const [deleteNotificationId, setDeleteNotificationId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  // store states & actions (only for selections, not filters)
  const selectedIds = useNotificationsStore(state => state.selectedNotificationIds)
  const toggleSelection = useNotificationsStore(state => state.toggleNotificationSelection)
  const isSelected = useNotificationsStore(state => state.isNotificationSelected)
  const selectAll = useNotificationsStore(state => state.selectAllNotifications)
  const deselectAll = useNotificationsStore(state => state.deselectAllNotifications)

  // Parse filters from URL
  const filters = useMemo(() => {
    const params = new URLSearchParams(searchParams.toString())

    // Parse array parameters
    const typesParam = params.get('types')
    const types = typesParam ? typesParam.split(',').filter(Boolean) : undefined

    const readParam = params.get('read')
    const read = readParam ? readParam === 'true' : undefined

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      scopes: ['administration'],
      q: params.get('q') || '',
      types,
      read
    }
  }, [searchParams])

  const { data, isLoading, isError, error, refetch, isFetching } = useNotifications(filters)
  const { mutate: markAsRead } = useSetReadNotifications()
  const { mutate: deleteNotificationMutate, isPending: isDeleting } = useDeleteNotification()
  const { mutate: bulkDeleteMutate, isPending: isBulkDeleting } = useBulkDeleteNotifications()

  const totalPages = data?.pages ?? 1
  const currentPage = data?.page ?? filters.page ?? 1
  const pageSize = data?.size ?? filters.size ?? 20

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<typeof filters>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle array values (like types, statuses)
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else if (typeof value === 'string' && value !== '') {
          params.set(key, value)
        } else if (typeof value === 'number' || typeof value === 'boolean') {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<typeof filters>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/notifications')
  }

  const handleChangePage = (page: number) => {
    if (page < 1 || page > totalPages) {
      return
    }
    handleFiltersChange({ page })
  }

  const handleChangePageSize = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleMarkSelectedAsRead = () => {
    if (selectedIds.length === 0) {
      return
    }

    // Filter to only include unread notifications
    const unreadIds =
      data?.items.filter(item => selectedIds.includes(item.id) && !item.readAt).map(item => item.id) || []

    if (unreadIds.length === 0) {
      return
    }

    markAsRead(
      { params: { ids: unreadIds } },
      {
        onSuccess: async () => {
          deselectAll()
          await refetch()

          queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
            if (!oldData) {
              return oldData
            }
            return {
              ...oldData,
              unreadTotal: Math.max(0, (oldData.unreadTotal || 0) - unreadIds.length),
              items: oldData.items.map((notif: any) =>
                unreadIds.includes(notif.id) ? { ...notif, readAt: new Date().toISOString() } : notif
              )
            }
          })

          await queryClient.invalidateQueries({
            queryKey: ['dropdown-notifications'],
            refetchType: 'none'
          })

          // Decrement unread notifications count in dashboard cache
          queryClient.setQueryData<DashboardResponseType>(['dashboard'], old => {
            if (!old?.stats) {
              return old
            }
            return {
              ...old,
              stats: {
                ...old.stats,
                unreadNotifications: Math.max(0, (old.stats.unreadNotifications ?? 0) - unreadIds.length)
              }
            }
          })
        }
      }
    )
  }

  const handleBulkDelete = () => {
    if (selectedIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmDeleteMessage = () => {
    if (deleteNotificationId) {
      deleteNotificationMutate(deleteNotificationId, {
        onSuccess: async () => {
          toast.success(t('deleted_successfully'))
          setDeleteNotificationId(null)
          await queryClient.invalidateQueries({ queryKey: ['notifications'] })

          const deletedItem = data?.items.find(item => item.id === deleteNotificationId)
          queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
            if (!oldData) {
              return oldData
            }
            const wasUnread = deletedItem && !deletedItem.readAt
            return {
              ...oldData,
              total: Math.max(0, (oldData.total || 0) - 1),
              unreadTotal: wasUnread ? Math.max(0, (oldData.unreadTotal || 0) - 1) : oldData.unreadTotal,
              items: oldData.items.filter((notif: any) => notif.id !== deleteNotificationId)
            }
          })

          await queryClient.invalidateQueries({
            queryKey: ['dropdown-notifications'],
            refetchType: 'none'
          })
        },
        onError: async (error: any) => {
          // If 404, item already deleted - just update cache without showing error
          if (error.response?.status === 404) {
            setDeleteNotificationId(null)
            await queryClient.invalidateQueries({ queryKey: ['notifications'] })

            const deletedItem = data?.items.find(item => item.id === deleteNotificationId)
            queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
              if (!oldData) {
                return oldData
              }
              const wasUnread = deletedItem && !deletedItem.readAt
              return {
                ...oldData,
                total: Math.max(0, (oldData.total || 0) - 1),
                unreadTotal: wasUnread ? Math.max(0, (oldData.unreadTotal || 0) - 1) : oldData.unreadTotal,
                items: oldData.items.filter((notif: any) => notif.id !== deleteNotificationId)
              }
            })

            await queryClient.invalidateQueries({
              queryKey: ['dropdown-notifications'],
              refetchType: 'none'
            })
          } else {
            toast.error(error.response?.data.message ?? t('error_deleting'))
            setDeleteNotificationId(null)
          }
        }
      })
    }
  }

  const confirmBulkDeleteNotifications = () => {
    if (selectedIds.length > 0) {
      bulkDeleteMutate(selectedIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['notifications'] })

          if (response.deletedItems && response.deletedItems.length > 0) {
            const deletedUnreadCount =
              data?.items.filter(item => response.deletedItems.includes(item.id) && !item.readAt).length || 0

            queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
              if (!oldData) {
                return oldData
              }
              return {
                ...oldData,
                total: Math.max(0, (oldData.total || 0) - response.deletedItems.length),
                unreadTotal: Math.max(0, (oldData.unreadTotal || 0) - deletedUnreadCount),
                items: oldData.items.filter((notif: any) => !response.deletedItems.includes(notif.id))
              }
            })

            await queryClient.invalidateQueries({
              queryKey: ['dropdown-notifications'],
              refetchType: 'none'
            })

            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: async (error: any) => {
          if (error.response) {
            // If 404, items already deleted - just update cache without showing error
            if (error.response.status === 404) {
              await queryClient.invalidateQueries({ queryKey: ['notifications'] })

              const deletedUnreadCount =
                data?.items.filter(item => selectedIds.includes(item.id) && !item.readAt).length || 0

              queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
                if (!oldData) {
                  return oldData
                }
                return {
                  ...oldData,
                  total: Math.max(0, (oldData.total || 0) - selectedIds.length),
                  unreadTotal: Math.max(0, (oldData.unreadTotal || 0) - deletedUnreadCount),
                  items: oldData.items.filter((notif: any) => !selectedIds.includes(notif.id))
                }
              })

              await queryClient.invalidateQueries({
                queryKey: ['dropdown-notifications'],
                refetchType: 'none'
              })
            } else if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  // Check if there are any unread items in the selected notifications
  const hasUnreadInSelected =
    selectedIds.length > 0 && data?.items.some(item => selectedIds.includes(item.id) && !item.readAt)

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BellRingIcon className="h-5 w-5" />
                {t('notifications')}
              </CardTitle>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {selectedIds.length > 0 && (
                <Button type="button" variant="destructive" onClick={handleBulkDelete}>
                  <TrashIcon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedIds.length})</span>
                  </div>
                </Button>
              )}
              {selectedIds.length > 0 && hasUnreadInSelected && (
                <Button type="button" variant="default" onClick={handleMarkSelectedAsRead}>
                  <CheckCheckIcon className="h-4 w-4" />
                  <span className="hidden lg:flex">{t('mark_as_read')}</span>
                </Button>
              )}
              {data && data.items.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => (selectedIds.length === data.items.length ? deselectAll() : selectAll(data.items))}>
                  <CheckIcon className="h-4 w-4" />
                  <span className="hidden lg:flex">
                    {selectedIds.length === data.items.length ? t('deselect_all') : t('select_all')}
                  </span>
                </Button>
              )}
              <NotificationsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex flex-col divide-y rounded border">
              {Array.from({ length: 5 }).map((_, i) => (
                <NotificationSkeleton key={i} />
              ))}
            </div>
          ) : (
            <LoadingState isLoading={false} error={error?.message} onRetry={refetch}>
              {data && (
                <div className="flex flex-col divide-y rounded border">
                  {data.items.length > 0 ? (
                    <>
                      {data.items.map(item => (
                        <NotificationItem
                          key={item.id}
                          item={item}
                          isSelected={isSelected(item.id)}
                          onToggleSelect={toggleSelection}
                          onDeleteSuccess={refetch}
                          requireDeleteConfirmation={true}
                          onDeleteConfirmation={setDeleteNotificationId}
                          showCheckbox={true}
                          onNavigate={undefined}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="text-muted-foreground py-10 text-center text-sm">{t('no_notifications')}</div>
                  )}
                </div>
              )}
            </LoadingState>
          )}

          {/* Pagination */}
          {!isLoading && !isError && data && data.pages > 0 && (
            <div className="mt-6">
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                pageSize={pageSize}
                onPageChangeAction={handleChangePage}
                onPageSizeChangeAction={handleChangePageSize}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={deleteNotificationId !== null}
        onCancel={() => setDeleteNotificationId(null)}
        onConfirm={confirmDeleteMessage}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteNotifications}
        isDeleting={isBulkDeleting}
        description={t('bulk_delete_confirmation_message')}
      />
    </>
  )
}

export default List
